import {
  cloneElement,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type FocusEvent,
  type ReactElement,
} from "react";
import { createPortal } from "react-dom";
import { cn } from "@/lib/cn";

interface TooltipChildProps {
  "aria-describedby"?: string;
  "aria-label"?: string;
}

interface TooltipProps {
  label: string;
  children: ReactElement<TooltipChildProps>;
  side?: "top" | "bottom" | "left" | "right";
  className?: string;
  delayMs?: number;
}

interface TooltipPosition {
  left: number;
  top: number;
}

const VIEWPORT_GUTTER = 8;
const TOOLTIP_GAP = 8;

function clamp(
  value: number,
  minimum: number,
  maximum: number,
): number {
  return Math.min(Math.max(value, minimum), maximum);
}

export function Tooltip({
  label,
  children,
  side = "top",
  className,
  delayMs = 180,
}: TooltipProps) {
  const tooltipId = useId();
  const triggerWrapperRef =
    useRef<HTMLSpanElement>(null);
  const tooltipRef = useRef<HTMLSpanElement>(null);
  const openTimerRef = useRef<number | null>(null);
  const [open, setOpen] = useState(false);
  const [position, setPosition] =
    useState<TooltipPosition>({
      left: 0,
      top: 0,
    });

  const clearOpenTimer = () => {
    if (openTimerRef.current !== null) {
      window.clearTimeout(openTimerRef.current);
      openTimerRef.current = null;
    }
  };

  const showWithDelay = () => {
    clearOpenTimer();
    openTimerRef.current = window.setTimeout(() => {
      setOpen(true);
      openTimerRef.current = null;
    }, delayMs);
  };

  const showImmediately = () => {
    clearOpenTimer();
    setOpen(true);
  };

  const hide = () => {
    clearOpenTimer();
    setOpen(false);
  };

  const updatePosition = () => {
    const trigger = triggerWrapperRef.current;
    const tooltipElement = tooltipRef.current;

    if (!trigger || !tooltipElement) {
      return;
    }

    const triggerRect = trigger.getBoundingClientRect();
    const tooltipRect =
      tooltipElement.getBoundingClientRect();

    let left =
      triggerRect.left +
      triggerRect.width / 2 -
      tooltipRect.width / 2;

    let top =
      triggerRect.top -
      tooltipRect.height -
      TOOLTIP_GAP;

    if (side === "bottom") {
      top = triggerRect.bottom + TOOLTIP_GAP;
    }

    if (side === "left") {
      left =
        triggerRect.left -
        tooltipRect.width -
        TOOLTIP_GAP;

      top =
        triggerRect.top +
        triggerRect.height / 2 -
        tooltipRect.height / 2;
    }

    if (side === "right") {
      left = triggerRect.right + TOOLTIP_GAP;

      top =
        triggerRect.top +
        triggerRect.height / 2 -
        tooltipRect.height / 2;
    }

    left = clamp(
      left,
      VIEWPORT_GUTTER,
      window.innerWidth -
        tooltipRect.width -
        VIEWPORT_GUTTER,
    );

    top = clamp(
      top,
      VIEWPORT_GUTTER,
      window.innerHeight -
        tooltipRect.height -
        VIEWPORT_GUTTER,
    );

    setPosition({ left, top });
  };

  useLayoutEffect(() => {
    if (!open) {
      return;
    }

    updatePosition();

    const animationFrame =
      window.requestAnimationFrame(updatePosition);

    return () => {
      window.cancelAnimationFrame(animationFrame);
    };
  }, [label, open, side]);

  useEffect(() => {
    if (!open) {
      return;
    }

    const handleViewportChange = () =>
      updatePosition();

    const handleKeyDown = (
      event: KeyboardEvent,
    ) => {
      if (event.key === "Escape") {
        hide();
      }
    };

    window.addEventListener(
      "resize",
      handleViewportChange,
    );

    window.addEventListener(
      "scroll",
      handleViewportChange,
      true,
    );

    window.addEventListener(
      "keydown",
      handleKeyDown,
    );

    return () => {
      window.removeEventListener(
        "resize",
        handleViewportChange,
      );

      window.removeEventListener(
        "scroll",
        handleViewportChange,
        true,
      );

      window.removeEventListener(
        "keydown",
        handleKeyDown,
      );
    };
  }, [open]);

  useEffect(() => {
    return () => {
      clearOpenTimer();
    };
  }, []);

  const handleBlurCapture = (
    event: FocusEvent<HTMLSpanElement>,
  ) => {
    const nextFocusedElement = event.relatedTarget;

    if (
      nextFocusedElement instanceof Node &&
      event.currentTarget.contains(nextFocusedElement)
    ) {
      return;
    }

    hide();
  };

  const tooltipElement = open
    ? createPortal(
        <span
          ref={tooltipRef}
          id={tooltipId}
          role="tooltip"
          className="pointer-events-none fixed z-[9999] w-max max-w-64 rounded-lg bg-ink px-2.5 py-1.5 text-center text-xs font-semibold leading-4 text-white shadow-floating"
          style={{
            left: position.left,
            top: position.top,
          }}
        >
          {label}
        </span>,
        document.body,
      )
    : null;

  return (
    <>
      <span
        ref={triggerWrapperRef}
        className={cn("inline-flex", className)}
        onMouseEnter={showWithDelay}
        onMouseLeave={hide}
        onFocusCapture={showImmediately}
        onBlurCapture={handleBlurCapture}
      >
        {cloneElement(children, {
          "aria-describedby": open
            ? tooltipId
            : undefined,
          "aria-label":
            children.props["aria-label"] ?? label,
        })}
      </span>
      {tooltipElement}
    </>
  );
}
